<template>
  <div
    ref="containerRef"
    :style="blur > 0 ? { filter: `blur(${blur}px)` } : null"></div>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from 'vue';
import { Renderer, Program, Mesh, Color, Triangle } from 'ogl';

// Ported from vue-bits "Magic Rings" (https://vue-bits.dev/animations/magic-rings).
// The original ships on three.js — this build renders the same fragment shader
// through OGL, which the theme already bundles for Aurora.
const VERT = `#version 300 es
in vec2 position;
void main() {
  gl_Position = vec4(position, 0.0, 1.0);
}
`;

const FRAG = `#version 300 es
precision highp float;

uniform float uTime, uAttenuation, uLineThickness;
uniform float uBaseRadius, uRadiusStep, uScaleRate;
uniform float uOpacity, uNoiseAmount, uRotation, uRingGap;
uniform float uFadeIn, uFadeOut;
uniform float uMouseInfluence, uHoverAmount, uHoverScale, uParallax, uBurst;
uniform float uGlow;
uniform vec2 uResolution, uMouse;
uniform vec3 uColor, uColorTwo;
uniform int uRingCount;

out vec4 fragColor;

const float HP = 1.5707963;
const float CYCLE = 3.45;

float fade(float t) {
  return t < uFadeIn ? smoothstep(0.0, uFadeIn, t) : 1.0 - smoothstep(uFadeOut, CYCLE - 0.2, t);
}

float ring(vec2 p, float ri, float cut, float t0, float px) {
  float t = mod(uTime + t0, CYCLE);
  float r = ri + t / CYCLE * uScaleRate;
  float d = abs(length(p) - r);
  float a = atan(abs(p.y), abs(p.x)) / HP;
  float th = max(1.0 - a, 0.5) * px * uLineThickness;
  float h = (1.0 - smoothstep(th, th * 1.5, d)) + uGlow;
  d += pow(cut * a, 3.0) * r;
  return h * exp(-uAttenuation * d) * fade(t);
}

void main() {
  float px = 1.0 / min(uResolution.x, uResolution.y);
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution.xy) * px;
  float cr = cos(uRotation), sr = sin(uRotation);
  p = mat2(cr, -sr, sr, cr) * p;
  p -= uMouse * uMouseInfluence;
  float sc = mix(1.0, uHoverScale, uHoverAmount) + uBurst * 0.3;
  p /= sc;
  vec3 c = vec3(0.0);
  float rcf = max(float(uRingCount) - 1.0, 1.0);
  for (int i = 0; i < 10; i++) {
    if (i >= uRingCount) break;
    float fi = float(i);
    vec2 pr = p - fi * uParallax * uMouse;
    vec3 rc = mix(uColor, uColorTwo, fi / rcf);
    c = mix(c, rc, vec3(ring(pr, uBaseRadius + fi * uRadiusStep, pow(uRingGap, fi), i == 0 ? 0.0 : 2.95 * fi, px)));
  }
  c *= 1.0 + uBurst * 2.0;
  float n = fract(sin(dot(gl_FragCoord.xy + uTime * 100.0, vec2(12.9898, 78.233))) * 43758.5453);
  c += (n - 0.5) * uNoiseAmount;
  fragColor = vec4(c, max(c.r, max(c.g, c.b)) * uOpacity);
}
`;

const props = defineProps({
  color: {
    type: String,
    default: '#7cff67',
  },
  colorTwo: {
    type: String,
    default: '#42fcff',
  },
  speed: {
    type: Number,
    default: 1,
  },
  ringCount: {
    type: Number,
    default: 6,
  },
  attenuation: {
    type: Number,
    default: 10,
  },
  // Wide halo around each line. 1 is the vue-bits look; 0 draws the lines only,
  // which is what a light background needs.
  glow: {
    type: Number,
    default: 1,
  },
  lineThickness: {
    type: Number,
    default: 2,
  },
  baseRadius: {
    type: Number,
    default: 0.35,
  },
  radiusStep: {
    type: Number,
    default: 0.1,
  },
  scaleRate: {
    type: Number,
    default: 0.1,
  },
  opacity: {
    type: Number,
    default: 1,
  },
  blur: {
    type: Number,
    default: 0,
  },
  noiseAmount: {
    type: Number,
    default: 0.1,
  },
  rotation: {
    type: Number,
    default: 0,
  },
  ringGap: {
    type: Number,
    default: 1.5,
  },
  fadeIn: {
    type: Number,
    default: 0.7,
  },
  fadeOut: {
    type: Number,
    default: 0.5,
  },
  followMouse: {
    type: Boolean,
    default: false,
  },
  mouseInfluence: {
    type: Number,
    default: 0.2,
  },
  hoverScale: {
    type: Number,
    default: 1.2,
  },
  parallax: {
    type: Number,
    default: 0.05,
  },
  clickBurst: {
    type: Boolean,
    default: false,
  },
});

const containerRef = ref(null);

let renderer = null;
let program = null;
let animateId = 0;
let resizeObserver = null;
let teardown = null;

const mouse = [0, 0];
const smoothMouse = [0, 0];
let hoverAmount = 0;
let isHovered = false;
let burst = 0;

const initRings = () => {
  const container = containerRef.value;
  if (!container) {
    return null;
  }

  renderer = new Renderer({
    alpha: true,
    dpr: Math.min(window.devicePixelRatio, 2),
  });

  const { gl } = renderer;
  gl.clearColor(0, 0, 0, 0);

  const geometry = new Triangle(gl);
  if (geometry.attributes.uv) {
    delete geometry.attributes.uv;
  }

  const colorOne = new Color(props.color);
  const colorTwo = new Color(props.colorTwo);

  program = new Program(gl, {
    vertex: VERT,
    fragment: FRAG,
    transparent: true,
    uniforms: {
      uTime: { value: 0 },
      uAttenuation: { value: props.attenuation },
      uGlow: { value: props.glow },
      uResolution: { value: [1, 1] },
      uColor: { value: [colorOne.r, colorOne.g, colorOne.b] },
      uColorTwo: { value: [colorTwo.r, colorTwo.g, colorTwo.b] },
      uLineThickness: { value: props.lineThickness },
      uBaseRadius: { value: props.baseRadius },
      uRadiusStep: { value: props.radiusStep },
      uScaleRate: { value: props.scaleRate },
      uRingCount: { value: props.ringCount },
      uOpacity: { value: props.opacity },
      uNoiseAmount: { value: props.noiseAmount },
      uRotation: { value: (props.rotation * Math.PI) / 180 },
      uRingGap: { value: props.ringGap },
      uFadeIn: { value: props.fadeIn },
      uFadeOut: { value: props.fadeOut },
      uMouse: { value: [0, 0] },
      uMouseInfluence: { value: 0 },
      uHoverAmount: { value: 0 },
      uHoverScale: { value: props.hoverScale },
      uParallax: { value: props.parallax },
      uBurst: { value: 0 },
    },
  });

  const mesh = new Mesh(gl, { geometry, program });

  container.appendChild(gl.canvas);
  gl.canvas.style.display = 'block';
  gl.canvas.style.width = '100%';
  gl.canvas.style.height = '100%';

  const resize = () => {
    const width = Math.max(container.clientWidth, 1);
    const height = Math.max(container.clientHeight, 1);
    renderer.setSize(width, height);
    program.uniforms.uResolution.value = [gl.canvas.width, gl.canvas.height];
  };

  resize();
  window.addEventListener('resize', resize);
  resizeObserver = new ResizeObserver(resize);
  resizeObserver.observe(container);

  // The canvas usually sits behind content and under `pointer-events: none`,
  // so it never receives mouse events itself — track on the document and test
  // the pointer against the canvas box instead.
  const isInside = (event) => {
    const rect = container.getBoundingClientRect();
    return (
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom
    );
  };

  const onMouseMove = (event) => {
    const rect = container.getBoundingClientRect();
    isHovered = isInside(event);
    if (!isHovered) {
      mouse[0] = 0;
      mouse[1] = 0;
      return;
    }
    mouse[0] = (event.clientX - rect.left) / rect.width - 0.5;
    mouse[1] = -((event.clientY - rect.top) / rect.height - 0.5);
  };

  const onClick = (event) => {
    if (isInside(event)) {
      burst = 1;
    }
  };

  const usesPointer = props.followMouse || props.clickBurst;
  if (usesPointer) {
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('click', onClick);
  }

  const update = (time) => {
    animateId = requestAnimationFrame(update);

    smoothMouse[0] += (mouse[0] - smoothMouse[0]) * 0.08;
    smoothMouse[1] += (mouse[1] - smoothMouse[1]) * 0.08;
    hoverAmount += ((isHovered ? 1 : 0) - hoverAmount) * 0.08;
    burst *= 0.95;
    if (burst < 0.001) {
      burst = 0;
    }

    program.uniforms.uTime.value = time * 0.001 * props.speed;
    program.uniforms.uMouse.value = [smoothMouse[0], smoothMouse[1]];
    program.uniforms.uMouseInfluence.value = props.followMouse
      ? props.mouseInfluence
      : 0;
    program.uniforms.uHoverAmount.value = hoverAmount;
    program.uniforms.uBurst.value = props.clickBurst ? burst : 0;

    renderer.render({ scene: mesh });
  };

  animateId = requestAnimationFrame(update);

  return () => {
    cancelAnimationFrame(animateId);
    window.removeEventListener('resize', resize);
    resizeObserver?.disconnect();
    if (usesPointer) {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('click', onClick);
    }
    if (gl.canvas.parentNode === container) {
      container.removeChild(gl.canvas);
    }
    gl.getExtension('WEBGL_lose_context')?.loseContext();
    renderer = null;
    program = null;
  };
};

onMounted(() => {
  teardown = initRings();
});

onUnmounted(() => {
  teardown?.();
  teardown = null;
});
</script>

<style scoped>
div {
  width: 100%;
  height: 100%;
}
</style>
